import generatedAgentCatalog from '../generated/agent-catalog.generated.json';

export interface AgentCatalogLoadingIndicator {
  name: string;
  description: string;
}

export interface AgentCatalogEntry {
  id: string;
  slug: string;
  name: string;
  role: string;
  roleLabel: string;
  tagline: string;
  description: string;
  category: 'market' | 'plan' | 'sell' | 'build' | 'support';
  index: number;
  features: string[];
  tasks: string[];
  specialties: string[];
  avatarColumn: number;
  avatarRow: number;
  accent: string;
  body: string;
  systemPrompt: string;
  sassyLoadingIndicators?: AgentCatalogLoadingIndicator[];
}

const agentSpritePath = 'https://platfio.com/assets/agents/platfio-agents-grid.png';
const agentSpriteColumns: number = 5;
const agentSpriteRows: number = 3;

const categoryIcons: Record<AgentCatalogEntry['category'], string> = {
  market: 'fa-bullhorn',
  plan: 'fa-diagram-project',
  sell: 'fa-handshake',
  build: 'fa-code',
  support: 'fa-life-ring',
};

export const agentCatalog: AgentCatalogEntry[] = (generatedAgentCatalog as AgentCatalogEntry[])
  .slice()
  .sort((left, right) => left.index - right.index);

const agentEntriesByKey = new Map<string, AgentCatalogEntry>();
agentCatalog.forEach((agent) => {
  agentEntriesByKey.set(agent.slug, agent);
  agentEntriesByKey.set(agent.id, agent);
  agentEntriesByKey.set(agent.name.toLowerCase(), agent);
  agentEntriesByKey.set(agent.roleLabel.toLowerCase(), agent);
});

export const defaultAgentSlug = 'account-executive';

export function getAgentSpritePosition(column: number, row: number): string {
  const x = agentSpriteColumns === 1 ? 50 : (column / (agentSpriteColumns - 1)) * 100;
  const y = agentSpriteRows === 1 ? 50 : (row / (agentSpriteRows - 1)) * 100;
  return `${x}% ${y}%`;
}

export function getAgentPortraitStyle(agentKey?: string | null): string {
  const agent = getAgentEntry(agentKey);

  if (!agent) {
    return [
      'background-image: radial-gradient(circle at 24% 20%, rgba(255,255,255,0.9), transparent 34%), linear-gradient(135deg, #2563eb, #0f172a);',
      'background-size: cover, cover;',
      'background-position: center, center;',
      'background-repeat: no-repeat;',
    ].join(' ');
  }

  return [
    `background-image: url('${agentSpritePath}'), radial-gradient(circle at 24% 20%, rgba(255,255,255,0.9), transparent 34%), linear-gradient(135deg, ${agent.accent}, #0f172a);`,
    `background-size: ${agentSpriteColumns * 100}% ${agentSpriteRows * 100}%, cover, cover;`,
    `background-position: ${getAgentSpritePosition(agent.avatarColumn, agent.avatarRow)}, center, center;`,
    'background-repeat: no-repeat;',
  ].join(' ');
}

export function getAgentEntry(agentKey?: string | null): AgentCatalogEntry | undefined {
  if (!agentKey) {
    return undefined;
  }

  return agentEntriesByKey.get(agentKey) || agentEntriesByKey.get(agentKey.toLowerCase());
}

export function getAgentName(agentKey?: string | null, fallback = 'Agent'): string {
  return getAgentEntry(agentKey)?.name || fallback;
}

export function getAgentRoleLabel(agentKey?: string | null, fallback = 'Agent'): string {
  return getAgentEntry(agentKey)?.roleLabel || fallback;
}

export function getAgentSystemPrompt(agentKey?: string | null): string {
  return getAgentEntry(agentKey)?.systemPrompt || '';
}

export function getAgentLoadingIndicators(agentKey?: string | null): AgentCatalogLoadingIndicator[] {
  return getAgentEntry(agentKey)?.sassyLoadingIndicators || [];
}

export function getAgentCategoryIcon(agentKey?: string | null): string {
  const category = getAgentEntry(agentKey)?.category;
  return category ? categoryIcons[category] : 'fa-user';
}

export const agentSelectorOptions = agentCatalog.map((agent) => ({
  value: agent.slug,
  label: agent.roleLabel,
  name: agent.name,
  subtitle: agent.roleLabel,
  accent: agent.accent,
  portraitStyle: getAgentPortraitStyle(agent.slug),
  systemPrompt: agent.systemPrompt,
  category: agent.category,
}));

export const agentSelectOptions = agentCatalog.map((agent) => ({
  value: agent.slug,
  label: agent.roleLabel,
}));

export const agentLabelMap = Object.fromEntries(agentCatalog.map((agent) => [agent.slug, agent.name]));

export const agentBoardColumns = agentCatalog.map((agent, index) => ({
  value: agent.slug,
  label: agent.name,
  icon: getAgentCategoryIcon(agent.slug),
  color: agent.accent,
  order: index + 1,
}));
